import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserContext } from "../UserContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { putClient } from "../API/clientAPI";
import { getJobOrders, putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import SignatureCanvas from "react-signature-canvas";
import { patchJobOrderLink } from "../API/jobOrderAPI";
import { getJobOrder } from "../API/jobOrderAPI";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from '@mui/icons-material/Circle';

// Import S3 configurations and bucket names
import {
  s3ReportsPDFUploader,
  REPORTS_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-reports";
import {
  s3InvoicePDFUploader,
  INVOICE_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-receipts";
import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

// Import PDF generation functions
import generateReceipts from "./GenerateReceipts";
import generateReports from "./GenerateReports";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function TransmittalForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    job_date: "",
    // end_date: "",
    start_time: "",
    // end_time: "",
    accomplished_by_technician: "",
    transmittal_notes: "",
  });

  const [client, setClient] = useState({});

  const [editedClient, setEditedClient] = useState();

  const [links, setLinks] = useState({});

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [editTechsOpen, setEditTechsOpen] = useState(false);
  const [editSelectedTechIds, setEditSelectedTechIds] = useState([]);

  const [technicianSignatory, setTechnicianSignatory] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client);

        const technician_signed = data?.technicians?.map(({ firstname, lastname }) => `${firstname} ${lastname}`) || [];

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          job_date: data?.job_date || "",
          // end_date: data?.end_date || "",
          start_time: data?.start_time || "",
          // end_time: data?.end_time || getNowHHMM(),   // default to current time
          accomplished_by_technician: technician_signed || []
        }));

        setValue("status", data?.status || "", { shouldValidate: true });
        setEditedClient(data.client);
      } catch (error) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id]);


  const TransmittalSchema = z.object({
    // uploaded URLs
    acknowledgedSignature: z.string().min(1, "Please click Upload for Acknowledged signature."),
    accomplishedSignature: z.string().min(1, "Please click Upload for Accomplished signature."),
    // proof that Upload was clicked (not just drawn)
    acknowledgedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Acknowledged signature."),
    accomplishedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Accomplished signature."),
    technician_signatory: z.string().trim().min(1, "Please choose a technician signatory."),
    acknowledgedClientSignatory: z.string().trim().min(1, "Please enter the client representative."),
    status: z
      .string()
      .refine(v => v && v !== "Not Done", {
        message: 'Please set Job Order Status to something other than "Not Done".',
      }),
  }).passthrough();

  const {
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    control,
    trigger,
    register,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(TransmittalSchema),
    mode: "onChange",
    defaultValues: {
      acknowledgedSignature: "",
      accomplishedSignature: "",
      acknowledgedUploaded: false,
      accomplishedUploaded: false,
      technician_signatory: "",
      acknowledgedClientSignatory: "",
      status: "",
    },
  });

  // Add new states for holding the signature data URLs
  const accomplishedBySignatureRef = useRef(null);
  const acknowledgedBySignatureRef = useRef(null);
  // Declare state for each signature URL
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");

  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("");

  // Track when client signature was uploaded (to catch technician delays)
  const [clientSignatureUploadedAt, setClientSignatureUploadedAt] = useState(null);
  // Track when technician signature was uploaded
  const [technicianSignatureUploadedAt, setTechnicianSignatureUploadedAt] = useState(null);

  // Function to recursively convert true/false to True/False in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };

  // const handleJObOrderChange = (field, value) => {
  //   setjobOrderParameters((prev) => {
  //     const newState = {
  //       ...prev,
  //       [field]: value,
  //     };

  //     // If changing the start date, ensure end date is not before start date
  //     if (field === 'job_date' && prev.end_date && value && value > prev.end_date) {
  //       newState.end_date = value; // Set end date to start date if it's earlier
  //     }

  //     // If changing the end date, ensure it's not before start date
  //     if (field === 'end_date' && prev.job_date && value && value < prev.job_date) {
  //       return prev; // Don't update if end date is before start date
  //     }

  //     return newState;
  //   });
  // };
  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters(prev => ({ ...prev, [field]: value }));
    if (field === "status") {
      setValue("status", value, { shouldValidate: true, shouldDirty: true });
      if (value !== "Not Done") clearErrors("status");
    }
  };


  // const job_types = [
  //   "GPC",
  //   "Fogging",
  //   "Monitoring",
  //   "Deep Cleaning",
  //   "QTM",
  //   "Heat Treatment",
  //   "Vacuuming",
  //   "Termite Control",
  //   "Comprehensive Termite",
  //   "Termite Baiting",
  //   "Termite Spot Spraying",
  //   "AG Installation",
  //   "Phase 1 and Phase 2",
  //   "QPMS",
  //   "Ant Control",
  //   "Cockroach Control",
  //   "Mosquito Control",
  //   "Cockroach Gel Bait/Powder",
  //   "Basic Rodent Control",
  //   "Intensive Rodent Control",
  //   "Termite Inspection",
  //   "Disinfection",
  //   "Billing",
  //   "Collection",
  //   "Deliver Service Invoice",
  //   "Deliver Certificate",
  //   "QuickCert",
  //   "Ocular",
  //   "Rodent Monitoring",
  //   "Daily Monitoring",
  //   "Logistics",
  //   "ILT Monitoring",
  //   "Facility Audit",
  //   "Meeting/Seminar",
  //   "QA Visit",
  //   "Termipore Installation",
  //   "In2Care Installation",
  //   "In2Care Monitoring",
  //   "Exterra Inground",
  //   "Delivery of Materials",
  //   "Backjob"
  // ]

  // const job_description = [
  //   "Spraying",
  //   "Misting",
  //   "Fogging",
  //   "Mistblower",
  //   "Rodent Trapping",
  //   "Rodent Monitoring",
  //   "Logistics (Billing, Collection, Delivery)",
  //   "AG Installation",
  //   "Comprehensive Termite Control",
  //   "Termatrac Monitoring",
  //   "Wiping",
  //   "Disinfection",
  //   "UV Light",
  //   "Cold Fogging",
  //   "AG Monitoring",
  //   "Powder/Gel Baiting",
  //   "Soil Poisoning",
  //   "AG Pullout",
  //   "QA Visit/Facility Audit",
  //   "GPC with Blower and Vacuum",
  //   "Termipore, In2Care, or Device Installation",
  //   "In2Care Monitoring"
  // ]

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));


  // for Edit Tech Dialog
  useEffect(() => {
    // fetch once
    getTechnicians().then(data => setAllTechnicians(data || [])).catch(console.error);
  }, []);

  const openEditTechs = () => {
    const ids = (jobOrder?.technicians || []).map(t => t.id);
    setEditSelectedTechIds(ids);
    setEditTechsOpen(true);
  };

  const closeEditTechs = () => setEditTechsOpen(false);

  const toggleEditTech = (techId) => (e) => {
    const checked = e.target.checked;
    setEditSelectedTechIds(prev => checked ? [...prev, techId] : prev.filter(id => id !== techId));
  };

  const saveEditTechs = async () => {
    const selected = (allTechnicians || [])
      .filter(t => editSelectedTechIds.includes(t.id))
      .map(({ id, firstname, lastname }) => ({ id, firstname, lastname }));

    try {
      await putJobOrder(jobOrder.id, { technicians: selected });
      // update local UI immediately
      setJobOrder(prev => ({ ...prev, technicians: selected }));

      // keep the names array in sync for your PDF payload
      setjobOrderParameters(prev => ({
        ...prev,
        accomplished_by_technician: selected.map(t => `${t.firstname} ${t.lastname}`)
      }));

      // ensure signatory stays valid
      const validNames = new Set(selected.map(t => `${t.firstname} ${t.lastname}`));
      const nextSignatory =
        technicianSignatory && validNames.has(technicianSignatory)
          ? technicianSignatory
          : "";

      setTechnicianSignatory(nextSignatory);
      setValue("technician_signatory", nextSignatory, { shouldValidate: true });

      setSuccessMessage("Technicians updated.");
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update technicians.");
    } finally {
      setEditTechsOpen(false);
    }
  };

  // End time helper
  const getNowHHMM = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Temporarily store the data URL
  // Save Accomplished By signature and upload to S3
  const saveAndUploadAccomplishedSignature = async () => {
    try {
      setIsLoading(true);

      // ⏰ CAPTURE TECHNICIAN SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setTechnicianSignatureUploadedAt(uploadTimestamp);

      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        accomplishedBySignatureRef,
        jobOrder.id,
        "accomplished"
      );

      // Update the state with the signature URL
      setAccomplishedSignatureURL(url);
      // ✅ reflect into RHF + prove Upload was clicked
      setValue("accomplishedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", true, { shouldValidate: true });
      clearErrors(["accomplishedSignature", "accomplishedUploaded"]);
      await trigger(["accomplishedSignature", "accomplishedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        technician_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      // Handle errors and show an error message
      setErrorMessage(`Error saving signature: ${error.message}`);
      setValue("accomplishedSignature", "", { shouldValidate: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
      await trigger(["accomplishedSignature", "accomplishedUploaded"]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear the Accomplished By signature without affecting the button states
  const clearAccomplishedSignature = () => {
    if (accomplishedBySignatureRef.current) {
      accomplishedBySignatureRef.current.clear();
      setAccomplishedSignatureURL("");
      setValue("accomplishedSignature", "", { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
      trigger(["accomplishedSignature", "accomplishedUploaded"]);
    }
  };

  // Save Acknowledged By signature and upload to S3
  const saveAndUploadAcknowledgedSignature = async () => {
    try {
      setIsLoading(true);
      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        acknowledgedBySignatureRef,
        jobOrder.id,
        "acknowledged"
      );

      // ⏰ CAPTURE CLIENT SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setClientSignatureUploadedAt(uploadTimestamp);

      // Update the state with the signature URL
      setAcknowledgedSignatureURL(url);
      console.log("Acknowledged Signature URL:", url);

      setValue("acknowledgedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", true, { shouldValidate: true });
      clearErrors(["acknowledgedSignature", "acknowledgedUploaded"]);
      await trigger(["acknowledgedSignature", "acknowledgedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        client_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      setErrorMessage("Error saving signature. Please try again.");
      setValue("acknowledgedSignature", "", { shouldValidate: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
      await trigger(["acknowledgedSignature", "acknowledgedUploaded"]);
    } finally {
      setIsLoading(false);
    }
  };
  // Clear the Acknowledged By signature
  const clearAcknowledgedSignature = () => {
    if (acknowledgedBySignatureRef.current) {
      acknowledgedBySignatureRef.current.clear();
      setAcknowledgedSignatureURL("");
      setValue("acknowledgedSignature", "", { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
      trigger(["acknowledgedSignature", "acknowledgedUploaded"]);
    }
  };

  const resizeAndUploadSignature = async (signatureRef, jobOrderId, type) => {
    // 1. grab the trimmed canvas
    if (!signatureRef.current) throw new Error("No signature canvas available");
    const canvas = signatureRef.current.getTrimmedCanvas();
    if (!canvas) throw new Error("Nothing to upload");

    // 2. draw it into a fixed‐size canvas
    const size = 200;
    const c2 = document.createElement("canvas");
    c2.width = size;
    c2.height = size;
    c2.getContext("2d").drawImage(canvas, 0, 0, size, size);

    // 3. turn into a blob, rejecting if null
    const blob = await new Promise((resolve, reject) => {
      c2.toBlob(b => (b ? resolve(b) : reject(new Error("Canvas toBlob returned null"))), "image/png");
    });

    // 4. upload
    const key = `${jobOrderId}-signature-${type}.png`;
    const params = {
      Bucket: TEMP_BUCKET_NAME,
      Key: key,
      Body: blob,
      ContentType: "image/png",
    };
    console.log("Uploading signature:", params);

    const resp = await s3TempBucketUploader.upload(params).promise();
    // AWS SDK v2 returns resp.Location; if not, you can fall back to the public URL:
    return resp.Location || `https://${params.Bucket}.s3.amazonaws.com/${params.Key}`;
  };

  const handleGeneratePDF = async (type) => {
    // Merge contact details with jobOrderParameters
    const pdfData = {
      ...jobOrderParameters,
      accomplishedSignature: accomplishedSignatureURL,
      acknowledgedSignature: acknowledgedSignatureURL,
    };

    console.log("Job Order Parameters:", pdfData);

    try {
      setIsLoading(true);
      const pdfBlob =
        type === "invoice"
          ? await generateReceipts(pdfData)
          : await generateReports(pdfData);
      if (!pdfBlob) {
        throw new Error("Failed to generate PDF blob.");
      }
      const s3Url = await uploadPDF(pdfBlob, jobOrder.id, type);
      console.log(`${type} PDF uploaded to S3 and DynamoDB updated:`, s3Url);

      // Prepare update data with the correct link field
      const updateData = {
        [`${type === "invoice" ? "receipts_url" : "reports_url"}`]: s3Url,
      };

      // Update DynamoDB with the S3 URL
      await patchJobOrderLink(jobOrder.id, updateData);
      console.log(`DynamoDB updated with ${type} URL:`, s3Url);
    } catch (error) {
      console.error("Error generating or uploading PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadPDF = async (pdfBlob, jobOrderId, type) => {
    console.log("Generated PDF Blob:", pdfBlob);

    const fileName = `${jobOrderId}-${type}.pdf`;
    const s3Uploader =
      type === "invoice" ? s3InvoicePDFUploader : s3ReportsPDFUploader;
    const params = {
      Bucket: type === "invoice" ? INVOICE_BUCKET_NAME : REPORTS_BUCKET_NAME,
      Key: `${fileName}`,
      Body: pdfBlob,
      ContentType: "application/pdf",
    };

    console.log("S3 Upload Parameters:", params); // Debugging line to view upload parameters

    try {
      await s3Uploader.upload(params).promise();
      return `https://${params.Bucket}.s3.amazonaws.com/${fileName}`;
    } catch (error) {
      console.error("S3 upload error:", error);
      throw new Error("Upload failed");
    }
  }; 

  // Function to format date
  // TODO: you can use date-fns for more advanced date formatting
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date format");
      }

      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch (error) {
      console.error(error.message);
      return "Invalid Date"; // Fallback for invalid dates
    }
  }

  const formattedDate = formatDate(jobOrderParameters.job_date);

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // Determine numeric dimensions based on breakpoints
  let canvasWidth, canvasHeight;
  if (isLg) {
    canvasWidth = 300;
    canvasHeight = 350;
  } else if (isMd) {
    canvasWidth = 250;
    canvasHeight = 325;
  } else {
    canvasWidth = 200;
    canvasHeight = 300;
  }

  let ackWidth, ackHeight;
  if (isLg) {
    ackWidth = 300;
    ackHeight = 150;
  } else if (isMd) {
    ackWidth = 250;
    ackHeight = 125;
  } else {
    ackWidth = 200;
    ackHeight = 100;
  }

  const onSubmit = async (formData) => {
    setIsLoading(true);

    const updatedJobOrderParameters = {
      ...jobOrderParameters,
      accomplishedSignature: formData.accomplishedSignature,
      acknowledgedSignature: formData.acknowledgedSignature,
      acknowledgedClientSignatory: formData.acknowledgedClientSignatory,
      technician_signatory: formData.technician_signatory,
      service_report_status: "Done",
      technicians: jobOrder?.technicians || [],
      accomplished_by_technician:
        jobOrderParameters?.accomplished_by_technician ||
        (jobOrder?.technicians || []).map(t => `${t.firstname} ${t.lastname}`), 
      // ⏰ TIMESTAMP TRACKING (prevents technician time-cheating)
      technician_signature_uploaded_at: technicianSignatureUploadedAt,  // When technician signs
      client_signature_uploaded_at: clientSignatureUploadedAt,  // When UPLOAD was clicked
      report_submitted_at: new Date().toISOString(),  // When SUBMIT was clicked
    };

    const { id, ...putClientParameters } = editedClient;

    //Added for Document Generation Receipts, Reports, Certificate
    try {
      // 1) transform & convert booleans
      const payload = convertBooleans({ jobOrder, updatedJobOrderParameters });
      // 2) call your PDF “field-mapper”
      const response = await submitDocumentFieldMapper(payload);
      console.log("Job order parameters submitted successfully:", response);

      setLinks(
        {
          receipts_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receipt.pdf`,
        }
      );

      setSuccessMessage(
        "Service Report generated successfully! Redirecting..."
      );

      setIsLoading(false);

      // 💾 SAVE ALL UPDATED PARAMETERS TO DATABASE (including timestamps)
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      await putClient(id, putClientParameters);
      // All parameters saved successfully

      // Redirect to service reports list after 2 seconds
      setTimeout(() => {
        navigate("/manage-service-reports");
      }, 2000);

    } catch (error) {
      console.error("Error submitting job order parameters:", error);
      setErrorMessage(
        `Failed to generate Service Report and Documents: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false);
    }
  }

  const onInvalid = () => {
    const first = document.querySelector(".MuiFormHelperText-root.Mui-error");
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  };


  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="job-order-form">
        <Grid container>
          {/* Basic Information Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginTop: { xs: "-5px", md: "5px", lg: "10px", xl: "20px" },
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Basic Information
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {" "}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <TextField
                      name="id"
                      label="Job Order ID"
                      value={jobOrder?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <FormControl fullWidth size="small" error={!!errors.status}>
                      <InputLabel id="job-order-status-label">
                        Job Order Status
                      </InputLabel>
                      <Select
                        labelId="job-order-status-label"
                        name="status"
                        value={jobOrderParameters?.status || ""}
                        label="Job Order Status"
                        onChange={(e) =>
                          handleJObOrderChange("status", e.target.value)
                        }
                      >
                        <MenuItem value="Not Done">Not Done</MenuItem>
                        <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                        <MenuItem value="Done">Done</MenuItem>
                        <MenuItem value="Cancelled By Client">
                          Cancelled By Client
                        </MenuItem>
                      </Select>
                      {errors.status && <FormHelperText>{errors.status.message}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client ID"
                      label="Client ID"
                      value={jobOrder?.client?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client Name"
                      label="Client Name"
                      value={jobOrder?.client?.name || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12} lg={12} xl={10}>
                        <TextField
                          label="Job Site Address"
                          variant="outlined"
                          fullWidth
                          value={
                            jobOrder?.client?.address
                              ? `${jobOrder?.client?.address.house_unit_street || ""
                              }, ${jobOrder?.client?.address.barangay || ""
                              }, ${jobOrder?.client?.address.city || ""
                              }, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder?.client?.address.zipcode || ""
                              }`
                              : ""
                          }
                          disabled
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={12} xl={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder?.client?.address?.area || ""}
                          fullWidth
                          disabled
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact
                          ? `${jobOrder?.client?.contact.lastname || ""}, ${jobOrder?.client?.contact.firstname || ""
                          }`
                          : ""
                      }
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      variant="outlined"
                      value={jobOrder?.client?.contact?.email?.email || ""}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact?.number
                          ? `${jobOrder?.client?.contact.number.type}: ${jobOrder?.client?.contact.number.number}`
                          : ""
                      }
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>           
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Type Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Job Type</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {selectedJobTypes.map(option => (
                    <Box
                      component="li"
                      key={option}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        mb: 0.5,
                      }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Description Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Job Description</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {selectedJobDescriptions.map(option => (
                    <Box
                      component="li"
                      key={option}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        mb: 0.5,
                      }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Special Notes Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder?.special_notes || ""}
                      fullWidth
                      disabled
                      multiline
                      rows={4}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Transmittal Notes */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Notes
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TextField
                  label="Notes"
                  value={jobOrderParameters?.transmittal_notes || ""}
                  onChange={e =>
                    handleJObOrderChange("transmittal_notes", e.target.value)
                  }
                  fullWidth
                  multiline
                  rows={6}
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Accomplished By and Acknowledged By Signature Section */}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Grid container spacing={3}>
                    {/* Service Accomplished by Section */}
                    <Grid item xs={12} md={12}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Service Accomplished by:
                          </Typography>
                          <Button size="small" variant="outlined" onClick={openEditTechs} sx={{ mb: 2 }}>
                            Edit technicians
                          </Button>
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Grid container spacing={1}>
                            {jobOrder?.technicians?.length > 0 ? (
                              jobOrder?.technicians.map((technician, index) => (
                                <Grid
                                  item
                                  xs={12}
                                  md={12}
                                  lg={12}
                                  xl={12}
                                  key={technician.id}
                                  mb={2}
                                >
                                  <TextField
                                    label={`Technician ${index + 1}`}
                                    variant="outlined"
                                    fullWidth
                                    value={`${technician.firstname} ${technician.lastname}`}
                                    disabled
                                    size="small"
                                  />
                                </Grid>
                              ))
                            ) : (
                              <Grid item xs={12} md={12} lg={12} xl={12}>
                                <TextField
                                  label="Technician"
                                  variant="outlined"
                                  fullWidth
                                  value="No Technicians Available"
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  size="small"
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>                          
                          <Box
                            sx={{
                              width: canvasWidth,
                              height: ackHeight,
                              border: "2px solid #000",
                              padding: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <SignatureCanvas
                              ref={accomplishedBySignatureRef}
                              penColor="black"
                              canvasProps={{
                                width: canvasWidth,
                                height: ackHeight,
                                className: "sigCanvas",
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Technician Signatory Dropdown */}
                      <Grid item xs={12}>
                        <FormControl fullWidth margin="normal" error={!!errors.technician_signatory}>
                          <InputLabel id="technician-signatory-label">
                            Technician Signatory
                          </InputLabel>
                          <Select
                            labelId="technician-signatory-label"
                            id="accomplished-by-technician"
                            value={technicianSignatory}
                            label="Technician Signatory"
                            onChange={(e) => {
                              const val = e.target.value;
                              setTechnicianSignatory(val); // keep local UI state
                              setValue("technician_signatory", val, { shouldValidate: true, shouldDirty: true }); // RHF/Zod
                              clearErrors("technician_signatory");
                            }}
                          >
                            {jobOrder?.technicians?.map((tech) => (
                              <MenuItem
                                key={tech.id}
                                value={`${tech.firstname} ${tech.lastname}`}
                              >
                                {`${tech.firstname} ${tech.lastname}`}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.technician_signatory && (
                            <FormHelperText error>{errors.technician_signatory.message}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <input type="hidden" {...register?.("technician_signatory")} />

                      {/* Action Buttons for Accomplished Signature */}
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        mt={2}
                        mb={2}
                        mr={2}
                      >
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={clearAccomplishedSignature}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#FF4C4C",
                            borderColor: "#FF4C4C",
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveAndUploadAccomplishedSignature}
                          sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                        >
                          Upload
                        </Button>
                      </Grid>
                      {(errors?.accomplishedUploaded || errors?.accomplishedSignature) && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors.accomplishedUploaded?.message || errors.accomplishedSignature?.message}
                        </FormHelperText>
                      )}

                      {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                      {!isTechnician && technicianSignatureUploadedAt && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                          <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                            🔵 Technician Signature Uploaded At:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                            {new Date(technicianSignatureUploadedAt).toLocaleString('en-US', {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: true
                            })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#1976d2', mt: 0.5, display: 'block' }}>
                            This timestamp is saved and cannot be changed.
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Schedule Section */}
            <Grid
              item
              xs={12}
              md={12}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                      sx={{                      
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        fontSize: "1rem",
                      }}
                    >
                      Schedule
                    </Typography>
                  }
                />
                <CardContent sx={{ flex: 1 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Start Date"
                        name="job_date"
                        type="date"
                        value={jobOrderParameters.job_date}
                        onChange={(e) =>
                          handleJObOrderChange("job_date", e.target.value)
                        }
                        fullWidth
                        size="small"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Start Time"
                        name="start_time"
                        type="time"
                        value={jobOrderParameters.start_time}
                        onChange={(e) =>
                          handleJObOrderChange("start_time", e.target.value)
                        }
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                      <TextField
                        label="End Date"
                        name="end_date"
                        type="date"
                        value={jobOrderParameters.end_date}
                        onChange={(e) =>
                          handleJObOrderChange("end_date", e.target.value)
                        }
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: {
                            min: jobOrderParameters.job_date || undefined
                          }
                        }}
                      />
                    </Grid> */}

                    {/* <Grid item xs={12} md={4}>
                      <TextField
                        label="End Time"
                        name="end_time"
                        type="time"
                        value={jobOrderParameters.end_time}
                        onChange={(e) =>
                          handleJObOrderChange("end_time", e.target.value)
                        }
                        fullWidth
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                onClick={() => handleJObOrderChange("end_time", getNowHHMM())}
                              >
                                Now
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid> */}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* For Client Only Section */}
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="subtitle2"
                                sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                      For Client Only
                    </Typography>
                  }
                  sx={{ px: 2, pt: 2, pb: 0 }}
                />
                <CardContent sx={{ px: 2, pb: 2 }}>                
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Acknowledged By:
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>                        
                        <Box
                          sx={{
                            width: ackWidth,
                            height: ackHeight,
                            border: "2px solid #000",
                            padding: 1,
                            marginTop: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <SignatureCanvas
                            ref={acknowledgedBySignatureRef}
                            penColor="black"
                            canvasProps={{
                              width: ackWidth,
                              height: ackHeight,
                              className: "sigCanvas",
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} sx={{ marginTop: 2 }}>
                      <Grid item xs={12} md={12} lg={12}>                        
                        <TextField
                          fullWidth
                          label="Client Representative"
                          name="Client Representative"
                          variant="outlined"
                          value={acknowledgedClientSignatory}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAcknowledgedClientSignatory(val); // keep local state
                            setValue("acknowledgedClientSignatory", val, { shouldValidate: true, shouldDirty: true }); // RHF/Zod
                            clearErrors("acknowledgedClientSignatory");
                          }}
                          size="small"
                        />
                        {errors.acknowledgedClientSignatory && (
                          <FormHelperText error sx={{ mt: 1 }}>
                            {errors.acknowledgedClientSignatory.message}
                          </FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                    <input type="hidden" {...register?.("acknowledgedClientSignatory")} />
                    
                    {/* Action Buttons for Acknowledged Signature */}
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      mt={2}
                      mb={2}
                      mr={2}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={clearAcknowledgedSignature}
                        sx={{
                          fontSize: "0.875rem",
                          color: "#FF4C4C",
                          borderColor: "#FF4C4C",
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveAndUploadAcknowledgedSignature}
                        sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                      >
                        Upload
                      </Button>
                    </Grid>
                    {(errors?.acknowledgedUploaded || errors?.acknowledgedSignature) && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {errors.acknowledgedUploaded?.message || errors.acknowledgedSignature?.message}
                      </FormHelperText>
                    )}

                    {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
                    {!isTechnician && clientSignatureUploadedAt && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                          ✅ Client Signature Uploaded At:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                          {new Date(clientSignatureUploadedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          })}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#558b2f', mt: 0.5, display: 'block' }}>
                          This timestamp is saved and cannot be changed.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Dialog for Edit Technicians */}
          <Dialog open={editTechsOpen} onClose={closeEditTechs} maxWidth="xs" fullWidth>
            <DialogTitle>Edit Technicians</DialogTitle>
            <DialogContent dividers>
              <Table size="small">
                <TableBody>
                  {allTechnicians.map(t => {
                    const notes = [];
                    if (t.competency_level?.driving_4_wheels && t.competency_level.driving_4_wheels !== "N/A") {
                      notes.push("Driver: 4 wheels");
                    }
                    if (t.competency_level?.driving_2_wheels && t.competency_level.driving_2_wheels !== "N/A") {
                      notes.push("Driver: 2 wheels");
                    }
                    const checked = editSelectedTechIds.includes(t.id);
                    return (
                      <TableRow key={t.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox checked={checked} onChange={toggleEditTech(t.id)} />
                        </TableCell>
                        <TableCell>{`${t.lastname}, ${t.firstname}`}</TableCell>
                        <TableCell>{notes.join(", ")}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeEditTechs}>Cancel</Button>
              <Button variant="contained" disabled={!editSelectedTechIds.length} onClick={saveEditTechs}>
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Display Receipts and Reports Links */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Document Links
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {links?.receipts_url ? (
                    <Grid item xs={12}>
                      <Box>
                        <Link
                          href={links.receipts_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          Receipts.pdf
                          <OpenInNewIcon
                            sx={{
                              marginLeft: 1,
                              fontSize: "1rem",
                            }}
                          />
                        </Link>
                      </Box>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body1" color="textSecondary">
                        No document links available.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Form buttons */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/manage-service-reports")}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default TransmittalForm;
